// LUMINA · pages/vajra/AdminHub.jsx
// Phase 39D: thin shell — sidebar nav + dispatches to per-section components in admin/
// Shared cross-section state (eventList / memberList / tags / teacherList / teachingList)
// lives here and is passed via props. Each section owns its own local form / msg state.

import React, { useState, useEffect, useCallback } from 'react';
import { API, GD, TXT2, ADMIN_MODULES } from './constants';

import OverviewSection from './admin/OverviewSection';
import EventSection from './admin/EventSection';
import RegistrationsSection from './admin/RegistrationsSection';
import PractitionersSection from './admin/PractitionersSection';
import TagsSection from './admin/TagsSection';
import CredentialsSection from './admin/CredentialsSection';
import TeachersSection from './admin/TeachersSection';
import TeachingsSection from './admin/TeachingsSection';
import FinanceSection from './admin/FinanceSection';
import EmailBlastSection, { SubscriberListView } from './admin/EmailBlastSection';
import OfferingMgmtSection from './admin/OfferingMgmtSection';
import PaymentConfigSection from './admin/PaymentConfigSection';
import SchedulesSection from './admin/SchedulesSection';
import ShopSection from './admin/ShopSection';
import PathManager from './admin/PathManager';
import TemplesSection from './admin/TemplesSection';
import TopicHubSection from './admin/TopicHubSection';
import ServiceRequestsSection from './admin/ServiceRequestsSection';
import ContactsSection from './admin/ContactsSection';

export default function AdminHub({ token, lang, T }) {
  const headers = {"Authorization":`Bearer ${token}`,"Content-Type":"application/json"};
  const [activeSection, setActiveSection] = useState("overview");

  // Cross-section shared state (read by ≥2 sections)
  const [eventList,    setEventList]    = useState([]);
  const [memberList,   setMemberList]   = useState([]);
  const [tags,         setTags]         = useState([]);
  const [teacherList,  setTeacherList]  = useState([]);
  const [teachingList, setTeachingList] = useState([]);
  const [templeList,   setTempleList]   = useState([]);
  const [hubList,      setHubList]      = useState([]);

  // Shared loader: sections that mutate event/member/tag data call this to refresh the shared state.
  const loadAdminData = useCallback(async () => {
    const [evs, mems, tgs] = await Promise.all([
      fetch(`${API}/vajra/staff/events?standalone=true`,{headers}).then(r=>r.ok?r.json():[]).catch(()=>[]),
      fetch(`${API}/vajra/admin/members`,{headers}).then(r=>r.ok?r.json():[]).catch(()=>[]),
      fetch(`${API}/vajra/admin/tags`,{headers}).then(r=>r.ok?r.json():[]).catch(()=>[]),
    ]);
    setEventList(Array.isArray(evs)?evs:evs.events||[]);
    setMemberList(Array.isArray(mems)?mems:[]);
    setTags(Array.isArray(tgs)?tgs:[]);
  }, [token]);

  const loadTeachers = useCallback(async () => {
    const d = await fetch(`${API}/vajra/teachers`).then(r=>r.ok?r.json():[]).catch(()=>[]);
    setTeacherList(Array.isArray(d)?d:[]);
  }, []);

  const loadTeachings = useCallback(async () => {
    const d = await fetch(`${API}/vajra/teachings`).then(r=>r.ok?r.json():[]).catch(()=>[]);
    setTeachingList(Array.isArray(d)?d:[]);
  }, []);

  const loadTemples = useCallback(async () => {
    const d = await fetch(`${API}/vajra/temples?per_page=100`).then(r=>r.ok?r.json():{temples:[]}).catch(()=>({temples:[]}));
    setTempleList(Array.isArray(d.temples)?d.temples:[]);
  }, []);

  const loadHubs = useCallback(async () => {
    const d = await fetch(`${API}/vajra/topic-hubs`).then(r=>r.ok?r.json():[]).catch(()=>[]);
    setHubList(Array.isArray(d) ? d : []);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Section-driven prefetch (preserves the original behaviour).
  useEffect(()=>{
    const s = activeSection;
    if (["event","registrations","tags","finance","credentials","email_blast"].includes(s)) loadAdminData();
    if (["teachers","event","teachings","schedules"].includes(s)) loadTeachers();
    if (["teachings","event","schedules"].includes(s)) loadTeachings();
    if (["temples","event"].includes(s)) loadTemples();
    if (["hub_section","event","teachings","shop"].includes(s)) loadHubs();
  }, [activeSection, loadAdminData, loadTeachers, loadTeachings, loadHubs]);

  const sharedProps = {
    token, headers,
    eventList, setEventList,
    memberList, setMemberList,
    tags, setTags,
    teacherList, setTeacherList,
    teachingList, setTeachingList,
    templeList, setTempleList,
    hubList,
    loadAdminData,
  };

  return (
    <div style={{padding:"24px 0"}}>
      <div style={{marginBottom:20}}>
        <div style={{fontSize:18,color:GD,letterSpacing:.5,marginBottom:4}}>{lang === "en" ? "Admin Hub" : "后台管理"}</div>
        <div style={{fontSize:12,color:"#bbb"}}>Admin Hub · 内部管理</div>
      </div>

      <div style={{display:"flex",gap:0,alignItems:"flex-start"}}>
        {/* Left Sidebar */}
        <div style={{width:180,flexShrink:0,borderRight:"1px solid rgba(212,175,55,0.2)",paddingRight:0}}>
          {ADMIN_MODULES.map(mod=>(
            <div key={mod.key} style={{marginBottom:8}}>
              <div style={{fontSize:11,color:GD,padding:"8px 12px 4px",letterSpacing:.5,fontWeight:600}}>
                {lang === "en" && mod.labelEn ? mod.labelEn : mod.label}
              </div>
              {mod.sections.map(sec=>(
                <div key={sec.key}
                  onClick={()=>setActiveSection(sec.key)}
                  style={{
                    padding:"7px 16px",fontSize:12,cursor:"pointer",
                    background:activeSection===sec.key?"rgba(139,105,20,0.12)":"transparent",
                    color:activeSection===sec.key?GD:TXT2,
                    borderLeft:activeSection===sec.key?`2px solid ${GD}`:"2px solid transparent",
                  }}>
                  {sec.label}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Right Content Panel */}
        <div style={{flex:1,paddingLeft:24,minWidth:0}}>
          {activeSection==="overview"           && <OverviewSection {...sharedProps}/>}
          {activeSection==="event"              && <EventSection {...sharedProps}/>}
          {activeSection==="registrations"      && <RegistrationsSection {...sharedProps}/>}
          {activeSection==="practitioners"      && <PractitionersSection token={token} headers={headers}/>}
          {activeSection==="service_requests"   && <ServiceRequestsSection token={token} headers={headers}/>}
          {activeSection==="subscribers" && <ContactsSection token={token} headers={headers}/>}
          {activeSection==="tags"               && <TagsSection {...sharedProps}/>}
          {activeSection==="credentials"        && <CredentialsSection {...sharedProps}/>}
          {activeSection==="teachers"           && <TeachersSection {...sharedProps}/>}
          {activeSection==="teachings"          && <TeachingsSection {...sharedProps}/>}
          {activeSection==="finance"            && <FinanceSection {...sharedProps}/>}
          {activeSection==="email_blast"        && <EmailBlastSection {...sharedProps}/>}
          {activeSection==="email_blast"        && <SubscriberListView token={token} headers={headers}/>}
          {activeSection==="offering_mgmt"      && <OfferingMgmtSection {...sharedProps}/>}
          {activeSection==="payment_config"     && <PaymentConfigSection {...sharedProps}/>}
          {activeSection==="schedules"          && <SchedulesSection {...sharedProps}/>}
          {activeSection==="shop"              && <ShopSection {...sharedProps}/>}
          {activeSection==="path_manager"      && <PathManager {...sharedProps}/>}
          {activeSection==="temples"           && <TemplesSection {...sharedProps}/>}
          {activeSection === "hub_section" && (
            <TopicHubSection token={token} headers={headers} teacherList={teacherList}/>
          )}
        </div>
      </div>
    </div>
  );
}
